import { PyodideInterface, loadPyodide } from 'pyodide'

export interface PyodideExecutionResult<T = unknown> {
    result: T | null
    error?: string
    executionTime: number
}

export interface PyodidePackageOptions {
    packages?: string[]
    micropip?: string[]
}

export interface PythonVariable {
    name: string
    value: unknown
}

/**
 * Defines how Python execution results should be returned
 * - 'stdout': Captures print() output and expressions (default) - user-friendly for interactive use
 * - 'result': Returns only the direct result from pyodide (undefined for print()) - fast for API calls
 */
export enum PythonReturnMode {
    StdOut = 'stdout',
    Result = 'result',
}

export type PythonExecutionOptions = {
    returnMode: PythonReturnMode
    timeout?: number // in seconds
}

export const INDEX_URL = 'https://cdn.jsdelivr.net/pyodide/v0.28.2/full/'

export const PACKAGE_INDEX_URL = 'https://cdn.jsdelivr.net/pyodide/v0.28.2/full/pyodide-lock.json'

const FULL_STDLIB = true
const DEFAULT_TIMEOUT = 15

export class PythonExecutor {
    private pyodide: PyodideInterface | null = null
    private isInitialized = false
    private initializationPromise: Promise<void> | null = null
    private globalVariables: Map<string, unknown> = new Map()
    private static instance: PythonExecutor | null = null

    constructor() {
        if (PythonExecutor.instance) {
            throw new Error('PythonExecuter instance already exists')
        }
    }

    public static getInstance(): PythonExecutor {
        if (!PythonExecutor.instance) {
            PythonExecutor.instance = new PythonExecutor()
        }
        return PythonExecutor.instance
    }

    /**
     * Initialize Pyodide with optional configuration
     * @returns Promise that resolves when Pyodide is initialized
     */
    public async initialize(): Promise<void> {
        if (this.isInitialized) {
            return Promise.resolve()
        }

        if (this.initializationPromise) {
            return this.initializationPromise
        }

        this.initializationPromise = this._initializePyodide()

        try {
            await this.initializationPromise
            this.isInitialized = true
        } catch (error) {
            this.initializationPromise = null
            throw error
        }
    }

    private async _initializePyodide(): Promise<void> {
        try {
            this.pyodide = await loadPyodide({
                indexURL: INDEX_URL,
                fullStdLib: FULL_STDLIB,
            })

            this.globalVariables.forEach((value, name) => {
                this.setGlobalVariable(name, value)
            })

            console.log('Pyodide initialized successfully')
        } catch (error) {
            console.error('Failed to initialize Pyodide:', error)
            throw new Error(`Pyodide initialization failed: ${error}`)
        }
    }

    /**
     * Execute Python code and return the result
     * @param code - The Python code to execute
     * @param options - Execution options including return mode and timeout
     * @returns Promise that resolves with the execution result
     */
    public async runPython<T = unknown>(
        code: string,
        options: PythonExecutionOptions = {
            returnMode: PythonReturnMode.StdOut,
            timeout: DEFAULT_TIMEOUT,
        }
    ): Promise<PyodideExecutionResult<T>> {
        await this.ensureInitialized()

        const startTime = performance.now()

        try {
            const executionPromise = Promise.resolve().then(() => {
                if (options.returnMode === PythonReturnMode.Result) {
                    const result = this.pyodide!.runPython(code)
                    const executionTime = performance.now() - startTime
                    return this.parseStructuredResult<T>(result, executionTime)
                } else {
                    const captureCode = this.createCaptureCode(code)
                    const result = this.pyodide!.runPython(captureCode)
                    const executionTime = performance.now() - startTime
                    return this.parseStructuredOutput<T>(result, executionTime)
                }
            })

            if (options.timeout && options.timeout > 0) {
                return await this.withTimeout(executionPromise, options.timeout * 1000, startTime)
            }

            return await executionPromise
        } catch (error) {
            const executionTime = performance.now() - startTime
            const errorMessage = error instanceof Error ? error.message : String(error)

            return {
                result: null,
                error: errorMessage,
                executionTime,
            }
        }
    }

    /**
     * Execute Python code asynchronously (for code with async/await)
     * @param code - The Python code to execute
     * @param options - Execution options including return mode and timeout
     * @returns Promise that resolves with the execution result
     */
    public async runPythonAsync<T = unknown>(
        code: string,
        options: PythonExecutionOptions = {
            returnMode: PythonReturnMode.StdOut,
            timeout: DEFAULT_TIMEOUT,
        }
    ): Promise<PyodideExecutionResult<T>> {
        await this.ensureInitialized()

        const startTime = performance.now()

        try {
            const executionPromise = (async () => {
                if (options.returnMode === PythonReturnMode.Result) {
                    const result = await this.pyodide!.runPythonAsync(code)
                    const executionTime = performance.now() - startTime

                    return this.parseStructuredResult<T>(result, executionTime)
                } else {
                    const captureCode = this.createCaptureCode(code)
                    const result = await this.pyodide!.runPythonAsync(captureCode)
                    const executionTime = performance.now() - startTime
                    return this.parseStructuredOutput<T>(result, executionTime)
                }
            })()

            if (options.timeout && options.timeout > 0) {
                return await this.withTimeout(executionPromise, options.timeout * 1000, startTime)
            }

            return await executionPromise
        } catch (error) {
            const executionTime = performance.now() - startTime
            const errorMessage = error instanceof Error ? error.message : String(error)

            return {
                result: null,
                error: errorMessage,
                executionTime,
            }
        }
    }

    /**
     * Install Python packages using micropip
     * @param packages - The list of Python packages to install
     * @returns Promise that resolves when packages are installed
     */
    public async installPackages(packages: string[]): Promise<void> {
        await this.ensureInitialized()

        try {
            await this.pyodide!.loadPackage('micropip')
            const micropip = this.pyodide!.pyimport('micropip')

            for (const pkg of packages) {
                await micropip.install(pkg)
                console.log(`Successfully installed package: ${pkg}`)
            }
        } catch (error) {
            console.error('Failed to install packages:', error)
            throw new Error(`Package installation failed: ${error}`)
        }
    }

    /**
     * Set a global variable in Python environment
     * @param name - The name of the global variable
     * @param value - The value of the global variable
     */
    public setGlobalVariable(name: string, value: unknown): void {
        this.globalVariables.set(name, value)

        if (this.isInitialized && this.pyodide) {
            this.pyodide.globals.set(name, this.toPython(value))
        }
    }

    /**
     * Set a JS object as a global variable WITHOUT conversion to Python.
     * This keeps the object as a JsProxy so Python can call methods on it.
     * Use this for proxy objects that need to expose methods to Python.
     *
     * @param name - The name of the global variable
     * @param value - The JS object to expose (will be accessible as JsProxy in Python)
     */
    public setJsProxy(name: string, value: unknown): void {
        if (!this.isInitialized || !this.pyodide) {
            this.globalVariables.set(name, value)
            return
        }

        this.pyodide.globals.set(name, value)
    }

    /**
     * Get a global variable from Python environment
     * @param name - The name of the global variable
     * @returns The value of the global variable
     */
    public getGlobalVariable(name: string): unknown {
        if (!this.isInitialized || !this.pyodide) {
            return this.globalVariables.get(name)
        }

        const value = this.pyodide.globals.get(name)
        return this.convertPyProxyToJS(value)
    }

    /**
     * Remove a global variable from Python environment
     * @param name - The name of the global variable
     */
    public removeGlobalVariable(name: string): void {
        this.globalVariables.delete(name)

        if (this.isInitialized && this.pyodide) {
            this.pyodide.globals.delete(name)
        }
    }

    /**
     * Get all global variables from Python environment
     * @returns The list of global variables
     */
    public getGlobalVariables(): PythonVariable[] {
        if (!this.isInitialized || !this.pyodide) {
            return Array.from(this.globalVariables.entries()).map(([name, value]) => ({
                name,
                value,
            }))
        }

        const variables: PythonVariable[] = []
        const globals = this.pyodide.globals.toJs()

        for (const [name, value] of globals) {
            variables.push({
                name,
                value: this.convertPyProxyToJS(value),
            })
        }

        return variables
    }

    /**
     * Execute Python code synchronously. Only use when Pyodide is guaranteed to be initialized.
     * @param code - The Python code to execute
     */
    public runPythonSync(code: string): void {
        if (!this.isInitialized || !this.pyodide) {
            console.warn('runPythonSync called before Pyodide is initialized')
            return
        }
        this.pyodide.runPython(code)
    }

    /**
     * Clear all global variables
     * @returns The list of global variables
     */
    public clearGlobalVariables(): void {
        this.globalVariables.clear()

        if (this.isInitialized && this.pyodide) {
            this.pyodide.globals.clear()
        }
    }

    /**
     * Import a Python module and return it
     * @param moduleName - The name of the Python module to import
     * @returns The imported module
     */
    public importModule(moduleName: string): unknown {
        if (!this.isInitialized || !this.pyodide) {
            throw new Error('Pyodide is not initialized')
        }

        try {
            return this.pyodide.pyimport(moduleName)
        } catch (error) {
            throw new Error(`Failed to import module '${moduleName}': ${error}`)
        }
    }

    /**
     * Convert JavaScript object to Python object
     * @param jsObject - The JavaScript object to convert
     * @returns The Python object
     */
    public toPython(jsObject: unknown): unknown {
        if (!this.isInitialized || !this.pyodide) {
            throw new Error('Pyodide is not initialized')
        }

        return this.pyodide.toPy(jsObject)
    }

    /**
     * Convert Python object to JavaScript object
     * @param pyObject - The Python object to convert
     * @returns The JavaScript object
     */
    public toJS(pyObject: unknown): unknown {
        if (!this.isInitialized || !this.pyodide) {
            throw new Error('Pyodide is not initialized')
        }

        return this.convertPyProxyToJS(pyObject)
    }

    /**
     * Check if Pyodide is initialized
     * @returns True if Pyodide is initialized, false otherwise
     */
    public get initialized(): boolean {
        return this.isInitialized
    }

    /**
     * Check if Pyodide is currently initializing
     * @returns True if Pyodide is currently initializing, false otherwise
     */
    public get initializing(): boolean {
        return this.initializationPromise !== null && !this.isInitialized
    }

    /**
     * Get Pyodide version
     * @returns The Pyodide version
     */
    public getVersion(): string | null {
        return this.pyodide?.version || null
    }

    /**
     * Restart Pyodide (destroy current instance and reinitialize)
     * @returns Promise that resolves when Pyodide is restarted
     */
    public async restart(): Promise<void> {
        this.destroy()
        await this.initialize()
    }

    /**
     * Destroy Pyodide instance and clean up resources
     */
    public destroy(): void {
        if (this.pyodide) {
            try {
                this.pyodide = null
            } catch (error) {
                console.warn('Error destroying Pyodide instance:', error)
            }
        }

        this.isInitialized = false
        this.initializationPromise = null
        this.globalVariables.clear()
    }

    private async ensureInitialized(): Promise<void> {
        if (!this.isInitialized) {
            await this.initialize()
        }
    }

    private createCaptureCode(code: string): string {
        const b64 = btoa(
            new TextEncoder()
                .encode(code)
                .reduce((data, byte) => data + String.fromCharCode(byte), '')
        )
        return `
import sys
import io
import json
from contextlib import redirect_stdout, redirect_stderr
import base64

# Create string buffers to capture output and errors
__stdout_buffer = io.StringIO()
__stderr_buffer = io.StringIO()

# Execute user code with output capture
try:
    with redirect_stdout(__stdout_buffer), redirect_stderr(__stderr_buffer):
        __code = base64.b64decode("${b64}").decode("utf-8")
        try:
            __result = eval(__code)
        except SyntaxError:
            exec(__code)
            __result = None
    
    __stdout_content = __stdout_buffer.getvalue()
    __stderr_content = __stderr_buffer.getvalue()
    
    # Compose output string: stdout > expression result > success
    if __stdout_content.strip():
        __final_output = __stdout_content.strip()
    elif __result is not None:
        __final_output = str(__result)
    else:
        __final_output = "Code executed successfully"
    
    __final_error = __stderr_content.strip() or None
        
except Exception as e:
    import traceback
    __final_output = ""
    __final_error = f"{str(e)}\\n{traceback.format_exc()}"

json.dumps({"output": __final_output, "error": __final_error})
`
    }

    private parseStructuredOutput<T>(
        result: unknown,
        executionTime: number
    ): PyodideExecutionResult<T> {
        try {
            const jsonString = this.convertPyProxyToJS(result) as string
            const parsed = JSON.parse(jsonString)

            if (parsed.error) {
                return {
                    result: null,
                    error: parsed.error,
                    executionTime,
                }
            }

            return {
                result: parsed.output as T,
                executionTime,
            }
        } catch (error) {
            return {
                result: this.convertPyProxyToJS(result) as T,
                executionTime,
            }
        }
    }

    private parseStructuredResult<T>(
        result: unknown,
        executionTime: number
    ): PyodideExecutionResult<T> {
        try {
            const jsonString = this.convertPyProxyToJS(result) as string
            const parsed = JSON.parse(jsonString)

            if (parsed.error) {
                return {
                    result: null,
                    error: parsed.error,
                    executionTime,
                }
            }

            return {
                result: parsed as T,
                executionTime,
            }
        } catch (error) {
            return {
                result: this.convertPyProxyToJS(result) as T,
                executionTime,
            }
        }
    }

    private convertPyProxyToJS(pyObject: unknown): unknown {
        if (!pyObject || typeof pyObject !== 'object') {
            return pyObject
        }

        const proxy = pyObject as { toJs?: () => unknown }
        if (proxy.toJs && typeof proxy.toJs === 'function') {
            try {
                return (
                    proxy as unknown as {
                        toJs: (options?: {
                            dict_converter?: (
                                entries: [string, unknown][]
                            ) => Record<string, unknown>
                        }) => unknown
                    }
                ).toJs({ dict_converter: Object.fromEntries })
            } catch (error) {
                console.warn('Error converting PyProxy to JS:', error)
                return pyObject
            }
        }

        return pyObject
    }

    /**
     * Apply timeout to a promise
     * @param promise - The promise to apply timeout to
     * @param timeoutMs - Timeout in milliseconds
     * @param startTime - Start time for execution time calculation
     * @returns Promise that resolves with the result or rejects on timeout
     */
    private async withTimeout<T>(
        promise: Promise<PyodideExecutionResult<T>>,
        timeoutMs: number,
        startTime: number
    ): Promise<PyodideExecutionResult<T>> {
        const timeoutPromise = new Promise<PyodideExecutionResult<T>>((_, reject) => {
            setTimeout(() => {
                const executionTime = performance.now() - startTime
                reject({
                    result: null,
                    error: `Python execution timed out after ${timeoutMs / 1000} seconds`,
                    executionTime,
                })
            }, timeoutMs)
        })

        try {
            return await Promise.race([promise, timeoutPromise])
        } catch (error) {
            if (
                error &&
                typeof error === 'object' &&
                'error' in error &&
                'executionTime' in error
            ) {
                return error as PyodideExecutionResult<T>
            }
            throw error
        }
    }
}
